/**
 * Shipping Failures List Component
 *
 * Displays shipping failures for coordinator resolution.
 * FOLLOWS: OOSIssuesList.tsx patterns
 */
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Truck, CheckCircle, Clock, AlertTriangle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import type { RouterOutputs } from '@/lib/trpc';
import { useShippingFailures } from '@/features/packing-station/hooks/useShipping';
import { ResolveShippingFailureDialog } from './ResolveShippingFailureDialog';

const PAGE_SIZE = 25;

type FailureProvider = NonNullable<
  RouterOutputs['shipping']['getShippingFailures']['failures'][number]['provider']
>;

/**
 * Provider badge labels. StarShipIt rows are filtered out server-side (fixed in
 * StarShipIt itself), but the map stays exhaustive so a new provider fails
 * typecheck until it has a label.
 */
const PROVIDER_LABELS: Readonly<Record<FailureProvider, string>> = {
  starshipit: 'StarShipIt',
  portless: 'Portless',
  yunexpress: 'YunExpress',
};

const RESOLUTION_CONFIG = {
  RETRY_SUCCESS: { label: 'Retried', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  MANUAL_SHIP: { label: 'Shipped Manually', color: 'bg-green-50 text-green-700 border-green-200' },
  CANCELLED: { label: 'Cancelled', color: 'bg-red-50 text-red-700 border-red-200' },
} as const;

interface ShippingFailuresListProps {
  locationId: string;
}

export function ShippingFailuresList({ locationId }: ShippingFailuresListProps) {
  const { t } = useTranslation('production');
  const [showResolved, setShowResolved] = useState(false);
  const [page, setPage] = useState(1);
  const [resolveTarget, setResolveTarget] = useState<{
    failureId: string;
    orderName: string;
  } | null>(null);

  const { data, isLoading } = useShippingFailures(
    locationId,
    showResolved
  );

  const failures = useMemo(() => data?.failures ?? [], [data?.failures]);

  // Paginate
  const totalItems = failures.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paginatedFailures = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return failures.slice(start, start + PAGE_SIZE);
  }, [failures, safePage]);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-24 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div>
      {/* Controls */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Switch
            id="show-resolved"
            checked={showResolved}
            onCheckedChange={(checked) => {
              setShowResolved(checked);
              setPage(1);
            }}
          />
          <Label htmlFor="show-resolved" className="text-sm text-muted-foreground">
            {t('issues.shipping.showResolved')}
          </Label>
        </div>
        {data && data.unresolvedCount > 0 && (
          <Badge variant="destructive">
            {t('issues.shipping.unresolved', { count: data.unresolvedCount })}
          </Badge>
        )}
      </div>

      {/* Results */}
      {failures.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Truck className="h-12 w-12 text-muted-foreground" aria-hidden="true" />
          <p className="mt-4 text-lg font-medium">{t('issues.shipping.noFailures')}</p>
          <p className="text-sm text-muted-foreground">
            {t('issues.shipping.willAppearHere')}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {paginatedFailures.map((failure) => {
            const isResolved = !!failure.resolvedAt;
            const providerBadge = failure.provider ? PROVIDER_LABELS[failure.provider] : null;
            return (
              <Card
                key={failure.id}
                className={isResolved ? 'opacity-70' : undefined}
              >
                <CardContent className="py-4 px-5">
                  <div className="flex items-start justify-between gap-4">
                    {/* Left: Order info + error */}
                    <div className="flex-1 min-w-0 space-y-1.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">
                          {failure.orderName}
                        </span>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" aria-hidden="true" />
                          {formatDistanceToNow(new Date(failure.createdAt), { addSuffix: true })}
                        </span>
                        {/* Phase 51: provider badge — the coordinator's
                            resolution differs by carrier (contacting Portless
                            vs fixing the order for YunExpress). */}
                        {providerBadge && (
                          <Badge variant="secondary" className="text-xs">
                            {providerBadge}
                          </Badge>
                        )}
                        {failure.errorCode && (
                          <Badge variant="outline" className="text-xs">
                            {failure.errorCode}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground flex items-start gap-1.5">
                        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-destructive" aria-hidden="true" />
                        <span className="break-words">{failure.errorMessage}</span>
                      </p>
                    </div>

                    {/* Right: Status / Action */}
                    <div className="shrink-0">
                      {isResolved && failure.resolution ? (
                        <div className="flex items-center gap-1.5">
                          <CheckCircle className="h-4 w-4 text-green-600" aria-hidden="true" />
                          <Badge
                            variant="outline"
                            className={RESOLUTION_CONFIG[failure.resolution].color}
                          >
                            {RESOLUTION_CONFIG[failure.resolution].label}
                          </Badge>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            setResolveTarget({
                              failureId: failure.id,
                              orderName: failure.orderName,
                            })
                          }
                        >
                          {t('issues.resolve')}
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <p className="text-sm text-muted-foreground">
                {t('issues.pagination.showing', { from: (safePage - 1) * PAGE_SIZE + 1, to: Math.min(safePage * PAGE_SIZE, totalItems), total: totalItems })}
              </p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {t('issues.pagination.page', { current: safePage, total: totalPages })}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={safePage <= 1}
                  onClick={() => setPage(Math.max(1, safePage - 1))}
                >
                  {t('issues.pagination.previous')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={safePage >= totalPages}
                  onClick={() => setPage(Math.min(totalPages, safePage + 1))}
                >
                  {t('issues.pagination.next')}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Resolve Dialog */}
      {resolveTarget && (
        <ResolveShippingFailureDialog
          open={!!resolveTarget}
          onOpenChange={(open) => {
            if (!open) setResolveTarget(null);
          }}
          failureId={resolveTarget.failureId}
          locationId={locationId}
          orderName={resolveTarget.orderName}
        />
      )}
    </div>
  );
}
